import Vec3 from "./vec3";

export interface KeyState {
  left: boolean,
  right: boolean,
  up: boolean,
  down: boolean,
  ctrl: boolean,
  shift: boolean
}

export interface ExpositionState {
  serverTime: number,
  requirePing: boolean,
  participants: {
    [participantId: string]: ParticipantState
  }
}

export interface ChatMessage {
  participantId: string,
  name: string,
  message: string
}

export interface ChatState {
  messages: ChatMessage[]
}

export interface ProgressIndicator {
  position: Vec3,
  progressPercentage: number
}

export interface ParticipantState {
  name: string,
  ping: number,
  position: Vec3,
  direction: Vec3,
  isWalking: Boolean
  colors: ColorConfig
}

export interface Item {
  name: string,
  iconName: string
}

export interface LevelMeta {
  id: string,
  name: string,
  load: () => ExpositionHall
}

export enum LevelAssets {
  DEFAULT_ASSET,
  WALL_HORIZONTAL,
  WALL_VERTICAL,
  WALL_PILLAR,
  STL_DEMO,
  OBJECT_EGG,
  IMAGE_DISPLAY,
  OVEN,
  FREEZER,
  WHEAT_STATION,
  MIXER,
  TRASHBIN,
  CACTUS1,
  CACTUS2,
  COUCH,
  BOOKSHELF,
  CATERING,
  POUFS,
  PLANTS,
  TABLETENNIS,
  FAUCET,
  COUNTER,
  TABLE,
  BANNER
}

export enum Orientation {
  NORTH,
  SOUTH,
  WEST,
  EAST
}

export interface StaticObject {
  name: string,
  assetType: number,
  position: Vec3,
  isBlocking: boolean, // is it blocking the player when walking against it?
  isVisible: boolean,
  payload: string | undefined,
  orientation?: Orientation
}

export interface ParticipantStart {
  position: Vec3,
  direction: Vec3
}

export interface Ground {
  name: string,
  position: Vec3,
  width: number,
  height: number
}

export interface Light {
  name: string,
  position: Vec3,
  intensity: number
}

export interface Camera {
  name: string,
  position: Vec3,
  lookingAt: Vec3
}

export interface ExpositionHallDimension {
  tileSizeX: number,
  tileSizeZ: number
}

export interface Carpet {
  position: Vec3,
  sizeX: number,
  sizeZ: number,
  textureUrl: string
}

export interface ExpositionHall {
  id: string,
  name: string,
  camera: Camera,
  staticObjects: StaticObject[],
  participantStarts: ParticipantStart[],
  grounds: Ground[]
  booths: Booth[],
  dimension: ExpositionHallDimension,
  carpets: Carpet[]
}

export interface Booth {
  name: string,
  images: BoothImageDisplay[],
  videos: BoothVideoDisplay[],
  links: BoothLinkDisplay[]
  blogPosts: BoothBlogPostDisplay[]
}

export interface BoothImageDisplay {
  position: Vec3,
  url: string,
  thumbnail: string,
  orientation: Orientation
}

export interface BoothVideoDisplay {
  position: Vec3,
  url: string,
  thumbnail: string,
  orientation: Orientation
}

export interface BoothBlogPostDisplay {
  position: Vec3,
  imageUrl: string,
  blogPostUrl: string,
  thumbnail: string,
  description: string
  orientation: Orientation
}

export interface BoothLinkDisplay {
  position: Vec3,
  url: string
  description: string
}


export interface AsciiMap {
  topLeftOrigin: Vec3
  asciiMap: string
}

export interface ClientInfo {
  name: string,
  colors: ColorConfig
}

export interface PingMessage {
  serverTime: number
}

export enum ClientToServerSyncMessageType {
  KEY_STATE_UPDATE, // message with keyboard state (which keys are pressed)
  CLIENT_INFO, // message with information about the connected client (participantId, name, ...)
  PING,
  SEND_MESSAGE// message to send the received serverTime back to the server
}

export interface ClientToServerSyncMessage {
  type: ClientToServerSyncMessageType,
  expositionId: string,
  participantId: string | null, // new participants do not have a participantId yet
  data: any
}

export enum ServerToClientSyncMessageType {
  EXPOSITION_JOINED,
  EXPOSITION_STATE_UPDATE,
  NEW_MESSAGE,
  CONFIG_UPATE
}

export interface ServerToClientSyncMessage {
  type: ServerToClientSyncMessageType,
  data: any
}

export interface ExpositionOptions {
  timeLimit: number
}

export interface ResetOptions {
  password: string
}

export interface KickOptions {
  password: string,
  expositionId: string,
  participantId: string
}

export interface ConfigOptions {
  password: string,
  tickRateHz: number | undefined,
  interpolationEnabled: boolean,
  displayNamesEnabled: boolean,
  maxParticipants: number | undefined
}

export interface ColorConfig {
  hairColor: string,
  skinColor: string
  shirtColor: string
}

export interface TimeSync {
  serverTime: number,
  clientTime: number
}

export interface ConfigData {
  tickRateHz: number,
  pingRateHz: number,
  maxParticipants: number,
  stateInterpolationEnabled: boolean,
  displayNamesEnabled: boolean
}
